import { PropertyField } from '../system/field';

/**
 * Helper to find the next focusable element outside the component
 */
export const findNextFocusableElement = (
  element: HTMLElement,
  direction: 'forward' | 'backward' = 'forward',
): HTMLElement | null => {
  // All focusable elements
  const focusableElements = 'input, select, textarea';

  // Get all focusable elements in the document
  const allFocusables = Array.from(
    document.querySelectorAll(focusableElements),
  ) as HTMLElement[];

  // Filter out elements within our component
  const externalFocusables = allFocusables;

  // Find this element in the filtered list
  const filteredIndex = externalFocusables.indexOf(element);

  // Return the next or previous external element based on direction
  if (
    direction === 'forward' &&
    filteredIndex > -1 &&
    filteredIndex < externalFocusables.length - 1
  ) {
    return externalFocusables[filteredIndex + 1];
  } else if (direction === 'backward' && filteredIndex > 0) {
    return externalFocusables[filteredIndex - 1];
  }

  return null;
};

/**
 * Utility functions for focusing on form fields
 */

// Comprehensive list of focusable selectors
const FOCUSABLE_SELECTORS = [
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  'a[href]',
  '[tabindex]:not([tabindex="-1"])',
  '[role="button"]:not([disabled])',
  '[role="combobox"]:not([disabled])',
  '[role="textbox"]:not([disabled])',
  '[role="listbox"]:not([disabled])',
  '[contenteditable="true"]',
].join(', ');

/**
 * Focus on a field by its field key
 *
 * @param fieldKey - The field key (e.g., 'LIST_PRICE', 'SALE_TYPE')
 * @param options - Optional configuration
 * @returns true if focus was successful, false otherwise
 *
 * @example
 * // Focus on LIST_PRICE field
 * focusField('LIST_PRICE');
 *
 * // Focus on a field with smooth scroll
 * focusField('PROPERTY_TYPE', { scrollIntoView: true, smooth: true });
 */
export function focusField(
  fieldKey: PropertyField,
  options: {
    scrollIntoView?: boolean;
    smooth?: boolean;
    delay?: number;
  } = {},
): boolean {
  const { scrollIntoView = false, smooth = true, delay = 0 } = options;

  // Function to perform the focus
  const performFocus = () => {
    // Find the field wrapper by ID
    const fieldWrapper = document.getElementById(fieldKey);

    if (!fieldWrapper) {
      console.warn(`Field wrapper not found for key: ${fieldKey}`);
      return false;
    }

    let focusableElement: HTMLElement | null = null;

    // Priority order for finding focusable elements:

    // 1. MUI Autocomplete input
    focusableElement = fieldWrapper.querySelector(
      '.MuiAutocomplete-input',
    ) as HTMLElement;

    // 2. MUI Select (including multi-select) - check for this before other inputs
    if (!focusableElement) {
      focusableElement = fieldWrapper.querySelector(
        '.MuiSelect-select',
      ) as HTMLElement;
    }

    // 3. Regular text/number inputs (but not hidden radio/checkbox)
    if (!focusableElement) {
      focusableElement = fieldWrapper.querySelector(
        'input[type="text"], input[type="number"], input[type="email"], input[type="tel"], input:not([type])',
      ) as HTMLElement;
    }

    // 4. Textareas
    if (!focusableElement) {
      focusableElement = fieldWrapper.querySelector('textarea') as HTMLElement;
    }

    // 5. Native select elements
    if (!focusableElement) {
      focusableElement = fieldWrapper.querySelector('select') as HTMLElement;
    }

    // 6. Radio groups - special handling
    if (!focusableElement) {
      const radioGroup = fieldWrapper.querySelector('[role="radiogroup"]');
      if (radioGroup) {
        // Try to find the checked radio first
        focusableElement = radioGroup.querySelector(
          'input[type="radio"]:checked',
        ) as HTMLElement;

        // If no checked radio, get the first one
        if (!focusableElement) {
          focusableElement = radioGroup.querySelector(
            'input[type="radio"]',
          ) as HTMLElement;
        }
      }
    }

    // 7. Checkboxes (including MUI)
    if (!focusableElement) {
      focusableElement = fieldWrapper.querySelector(
        'input[type="checkbox"]',
      ) as HTMLElement;
    }

    // 8. Elements with role="combobox" (but not already found)
    if (!focusableElement) {
      focusableElement = fieldWrapper.querySelector(
        '[role="combobox"][tabindex="0"]',
      ) as HTMLElement;
    }

    // 9. Buttons (if that's the primary interaction)
    if (!focusableElement) {
      focusableElement = fieldWrapper.querySelector(
        'button:not([tabindex="-1"]):not(.MuiIconButton-root)',
      ) as HTMLElement;
    }

    // 10. Fallback to anything with explicit tabindex
    if (!focusableElement) {
      focusableElement = fieldWrapper.querySelector(
        '[tabindex="0"]',
      ) as HTMLElement;
    }

    // 11. Last resort - use the general selector
    if (!focusableElement) {
      focusableElement = fieldWrapper.querySelector(
        FOCUSABLE_SELECTORS,
      ) as HTMLElement;
    }

    if (!focusableElement) {
      console.warn(`No focusable element found in field: ${fieldKey}`);
      return false;
    }

    // Scroll into view if requested
    if (scrollIntoView) {
      fieldWrapper.scrollIntoView({
        behavior: smooth ? 'smooth' : 'auto',
        block: 'center',
        inline: 'nearest',
      });
    }

    // Focus the element
    try {
      // For disabled elements, we can't focus them
      if ((focusableElement as any).disabled) {
        console.warn(
          `Element in field ${fieldKey} is disabled and cannot be focused`,
        );
        return false;
      }

      // Clear any existing focus first (helps with some MUI components)
      if (document.activeElement && document.activeElement !== document.body) {
        (document.activeElement as HTMLElement).blur();
      }

      // Small delay to ensure blur completes
      setTimeout(() => {
        if (!focusableElement) return;
        focusableElement.focus();

        // For MUI components, sometimes we need to trigger additional events
        if (
          focusableElement.matches(
            '.MuiAutocomplete-input, .MuiInputBase-input',
          )
        ) {
          // Dispatch focus event to ensure MUI handlers run
          focusableElement.dispatchEvent(
            new FocusEvent('focus', { bubbles: true }),
          );

          // For autocomplete, also dispatch a click to ensure dropdown behavior works
          if (focusableElement.matches('.MuiAutocomplete-input')) {
            focusableElement.click();
          }
        }
      }, 10);

      return true;
    } catch (error) {
      console.error(`Error focusing field ${fieldKey}:`, error);
      return false;
    }
  };

  // If delay is specified, use setTimeout
  if (delay > 0) {
    setTimeout(performFocus, delay);
    return true; // Assume it will succeed
  }

  return performFocus();
}
